<template>
  <modal name="update-user" class="modal-inner modal-update-user" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="600" height="auto" :scrollable="true" :resizable="true" :adaptive="true" :reset="true" @before-close="resetForm">
    <span class="close-button" @click="hide('update-user')"><BaseIcon icon="times-circle" class="text-white"/></span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      <span v-if="updateInfo">{{ updateInfo.username }}</span>
    </div>
    <div class="modal-body">
      <ValidationObserver ref="formUpdateUser" v-if="updateInfo != null">
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.firstname" type="text" :field_name="$t('user_modal.first_name')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.lastname" type="text" :field_name="$t('user_modal.last_name')" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.email" type="text" :field_name="$t('user_modal.email')" rules="required|email" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.phone" type="text" :field_name="$t('user_modal.phone')" rules="required" />
          </div>
        </div>

        <!-- SecurOne Status Section -->
        <div class="mx-2 border-b border-secondary-400 my-5" v-if="updateInfo.securOne && user.role === 'admin'"></div>
        <div class="flex flex-wrap bg-gray-50 p-2 rounded" v-if="updateInfo.securOne && user.role === 'admin'">
          <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">SecurOne Integration</div>
          <div class="w-full md:w-1/2 px-2 py-1">
            <span class="text-xs font-bold text-gray-500">Status:</span>
            <span :class="{
              'text-green-600': updateInfo.securOne.status === 'success',
              'text-yellow-600': updateInfo.securOne.status === 'pending',
              'text-red-600': updateInfo.securOne.status === 'failed'
            }" class="ml-2 font-semibold uppercase text-xs">
              {{ updateInfo.securOne.status }}
            </span>
          </div>
          <div class="w-full md:w-1/2 px-2 py-1" v-if="updateInfo.securOne.securOneId">
            <span class="text-xs font-bold text-gray-500">ID:</span>
            <span class="ml-2 text-xs font-mono">{{ updateInfo.securOne.securOneId }}</span>
          </div>
          <div class="w-full px-2 py-1" v-if="updateInfo.securOne.lastSyncAt">
            <span class="text-xs font-bold text-gray-500">Last Sync:</span>
            <span class="ml-2 text-xs">{{ new Date(updateInfo.securOne.lastSyncAt).toLocaleString() }}</span>
          </div>
          
          <!-- Email Status -->
          <div class="w-full px-2 py-1 mt-1 border-t border-gray-200 pt-1">
             <span class="text-xs font-bold text-gray-500">Invitation Email:</span>
             <span v-if="updateInfo.securOne.emailSent === true" class="ml-2 text-xs font-bold text-green-600">
               SENT
             </span>
             <span v-else-if="updateInfo.securOne.emailSent === false" class="ml-2 text-xs font-bold text-red-600">
               FAILED
             </span>
             <span v-else class="ml-2 text-xs text-gray-400">
               Unknown
             </span>
             <div v-if="updateInfo.securOne.emailSent === false && updateInfo.securOne.emailError" class="text-xs text-red-500 mt-1">
               Error: {{ updateInfo.securOne.emailError }}
             </div>
          </div>

          <div class="w-full px-2 py-1 mt-2 bg-red-100 border border-red-200 rounded p-2" v-if="updateInfo.securOne.status === 'failed' && updateInfo.securOne.lastError">
            <div class="text-xs font-bold text-red-700">Error Details:</div>
            <div class="text-xs text-red-600 mt-1">{{ updateInfo.securOne.lastError.message }}</div>
            <div class="text-xs text-red-500 mt-1 font-mono" v-if="updateInfo.securOne.lastError.code">Code: {{ updateInfo.securOne.lastError.code }}</div>
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.username" type="text" :field_name="$t('user_modal.username')" :rules="/^(securone_basic|basic_user)$/.test(updateInfo.role) ? 'required' : 'required|alpha_dash'" disabled readonly />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="updateInfo.role" :options="filtered_roles" nameSelector="name" optionSelector="id" :field_name="$t('user_modal.role')" rules="required" @change="changeUserRole()" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="updateInfo.role == 'admin'">
            <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">Åtkomst till domän<b class="text-red-500 ml-1">*</b></div>
            <div class="flex flex-col space-y-2">
              <label class="inline-flex items-center">
                <input v-model="updateInfo.accessCountries" value="se" type="checkbox" class="form-checkbox h-4 w-4" />
                <span class="ml-2 text-sm">Sverige (.se)</span>
              </label>
              <label class="inline-flex items-center">
                <input v-model="updateInfo.accessCountries" value="no" type="checkbox" class="form-checkbox h-4 w-4" />
                <span class="ml-2 text-sm">Norge (.no)</span>
              </label>
            </div>
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5" v-if="updateInfo.role != 'admin'"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2" v-if="/^reseller|^superuser|^user|^securdesktop|^securone_basic|^basic_user/.test(updateInfo.role) && user.role == 'admin'">
            <BaseSelect v-model="updateInfo.reseller_id" :options="resellers" nameSelector="name" optionSelector="_id" field_name="Återförsäljare" :rules="/^reseller/.test(updateInfo.role) ? 'required' : ''" @change="changeReseller()" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="/^reseller/.test(updateInfo.role)">
            <BaseSelect v-model="updateInfo.reseller_id" :options="resellers" nameSelector="name" optionSelector="_id" field_name="Återförsäljare" :rules="/^reseller/.test(updateInfo.role) ? 'required' : ''" />
          </div>
          <div class="w-full md:w-1/3 flex flex-col px-2 py-2" v-if="updateInfo.role == 'superuser' || updateInfo.role == 'user' || updateInfo.role == 'securdesktop' || updateInfo.role == 'securone_basic' || updateInfo.role == 'basic_user'">
            <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("user_modal.customer") }}<b class="text-red-500 ml-1">*</b></div>
            <div class="w-full">
              <div class="mb-2">
                <input type="text" v-model.trim="customerSearch" placeholder="Sök" class="bg-gray-100 rounded w-full text-xs text-gray-900 focus:outline-none border-b border-gray-300 transition duration-500 px-2 py-2" />
              </div>
              <div class="max-h-80 overflow-y-scroll border border-gray-200 rounded px-2 py-2">
                <div v-if="filteredCustomer.length == 0" class="w-full text-xs italic text-gray-500 py-2">{{ $t("user_modal.no_cutomer_found") }}</div>
                <div class="px-2 transition ease-in-out duration-200" v-for="c in filteredCustomer" :key="c._id" v-bind:class="{ 'bg-gray-100': updateInfo.customers_id.indexOf(c.customer_id) > -1 || updateInfo.customers_id.indexOf(String(c._id)) > -1 }">
                  <label class="inline-flex items-center mt-2">
                    <input
                      v-if="updateInfo.role == 'superuser' || updateInfo.role == 'user'"
                      v-model="updateInfo.customers_id"
                      :value="c.customer_id"
                      type="checkbox"
                      class="form-checkbox h-4 w-4"
                      @change="
                        filterUnitByCustomer();
                        pruneUnits();
                      "
                    />
                    <input
                      v-if="updateInfo.role == 'securdesktop' || updateInfo.role == 'securone_basic' || updateInfo.role == 'basic_user'"
                      v-model="updateInfo.customers_id[0]"
                      :value="c.customer_id"
                      type="radio"
                      class="form-checkbox h-4 w-4"
                      @change="
                        filterUnitByCustomer();
                        updateInfo.units_id = [];
                      "
                    />
                    <span class="ml-2 text-sm font-sans font-semibold">{{ c.name }}</span>
                    <!-- SEC-334: a customer still stored on this user that the chosen reseller no
                         longer sells. Without this it is invisible in the form and survives every
                         save, so nobody can take it away. Untick it and save to remove it. -->
                    <span v-if="isStrandedCustomer(c)" class="ml-2 text-xs italic text-red-600">{{ $t("user_modal.customer_not_sold_by_reseller") }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div class="w-full md:w-1/3 flex flex-col px-2 py-2" v-if="updateInfo.role == 'user' || updateInfo.role == 'securdesktop' || updateInfo.role == 'securone_basic' || updateInfo.role == 'basic_user'">
            <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("user_modal.operation") }}<b class="text-red-500 ml-1">*</b></div>
            <div class="w-full">
              <div class="mb-2">
                <input type="text" v-model.trim="unitSearch" placeholder="Sök" class="bg-gray-100 rounded w-full text-xs text-gray-900 focus:outline-none border-b border-gray-300 transition duration-500 px-2 py-2" />
              </div>
              <div class="mb-2 px-2" v-if="filteredUnit.length > 0">
                <label class="inline-flex items-center">
                  <input type="checkbox" class="form-checkbox h-4 w-4" :checked="allListedUnitsSelected" @change="toggleAllUnits()" />
                  <span class="ml-2 text-sm font-sans font-semibold">{{ allListedUnitsSelected ? $t("user_modal.deselect_all") : $t("user_modal.select_all") }}</span>
                </label>
              </div>
              <div class="max-h-80 overflow-y-scroll border border-gray-200 rounded px-2 py-2">
                <div v-if="filteredUnit.length == 0" class="w-full text-xs italic text-gray-500 py-2">{{ $t("user_modal.no_department_found") }}</div>
                <div v-for="g in groupedFilteredUnits" :key="g.customer_id">
                  <div class="text-xs font-bold text-gray-500 uppercase mt-3 mb-1">{{ g.customer_name }}</div>
                  <div class="px-2 transition ease-in-out duration-200" v-for="u in g.units" :key="u._id" v-bind:class="{ 'bg-gray-100': updateInfo.units_id.indexOf(u.unit_id) > -1 }">
                    <label class="inline-flex items-center mt-2">
                      <input v-model="updateInfo.units_id" :value="u.unit_id" type="checkbox" class="form-checkbox h-4 w-4" /><span class="ml-2 text-sm font-sans font-semibold">{{ u.name }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- basic_user too: securcloud provisions a device for that role
               (DEVICE_ROLES), so hiding the panel hid a live SOS button's
               state and its opt-out from the one page that manages them. -->
          <div class="w-full md:w-1/3 flex flex-col px-2 py-2" v-if="hasVirtualDevicePanel">
            <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("create_user_page.mobile_alarm_device") }}</div>
            <label class="inline-flex items-start mt-1">
              <input v-model="virtualDeviceEnabled" type="checkbox" class="form-checkbox h-4 w-4 mt-1" />
              <span class="ml-2 text-sm font-sans font-semibold">{{ $t("create_user_page.create_mobile_alarm_device") }}</span>
            </label>
            <div class="text-xs text-gray-500 mt-2 leading-snug">{{ $t("create_user_page.mobile_alarm_device_help") }}</div>
            <!-- Clearing the box suspends the device rather than deleting it,
                 so the person's recipient configuration survives being turned
                 back on. The app only stops showing the button at their next
                 sign-in, which is what this line says. -->
            <div class="text-xs text-gray-500 mt-2 leading-snug italic" v-if="!virtualDeviceEnabled">{{ $t("create_user_page.mobile_alarm_device_off_note") }}</div>

            <!-- What the device is actually doing right now. Read from the
                 server rather than inferred from the checkbox: the addon can be
                 off, or the account inactive, and then the box is ticked while
                 no device exists. Showing the checkbox alone would tell an
                 administrator the alarm button works when it does not. -->
            <div class="mt-3 bg-gray-50 rounded px-2 py-2" v-if="virtualDeviceLoading || virtualDeviceLoaded">
              <div class="text-xs text-gray-500" v-if="virtualDeviceLoading">{{ $t("user_modal.virtual_device_loading") }}</div>
              <!-- A failed fetch is said, not shown as "no device": the two
                   call for different actions (retry versus check the addon). -->
              <div class="text-xs text-red-600" v-else-if="virtualDeviceError">{{ $t("user_modal.virtual_device_error") }}</div>
              <template v-else-if="virtualDevice">
                <div class="flex items-center justify-between">
                  <span class="text-xs font-bold text-gray-600 uppercase tracking-wide">{{ $t("user_modal.virtual_device_state") }}</span>
                  <span
                    class="text-xxs uppercase tracking-wide rounded px-1 py-0.5"
                    :class="virtualDevice.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'"
                  >
                    {{ virtualDevice.active ? $t("user_modal.virtual_device_active") : $t("user_modal.virtual_device_suspended") }}
                  </span>
                </div>
                <!-- A superuser reads a person who shares one of its customers,
                     and that person's one device can sit in a department of a
                     customer the superuser has no part in. securcloud then
                     answers outside_reach with the department, centre and
                     identifiers blanked, keeping only whether the button works.
                     Shown as one line in place of the two rows below: those
                     would read "None — the alarm goes to colleagues" and an
                     empty department, which is a claim, not a blank. The
                     "set on the device" hint goes too: that device is not one
                     this login can open. -->
                <div class="text-xs text-gray-600 mt-2 leading-snug" v-if="virtualDevice.outside_reach">{{ $t("user_modal.virtual_device_outside_reach") }}</div>
                <template v-else>
                  <div class="text-xs text-gray-600 mt-2">
                    {{ $t("user_modal.virtual_device_alarm_centre") }}:
                    <span class="font-semibold">{{ virtualDeviceAlarmCentreName }}</span>
                  </div>
                  <div class="text-xs text-gray-600 mt-1">
                    {{ $t("create_user_page.department") }}:
                    <span class="font-semibold">{{ virtualDeviceUnitName }}</span>
                  </div>
                  <!-- Recipients, SMS and templates are configured on the device
                       itself, under its department. Named rather than linked: the
                       device page is addressed by four route params this modal
                       does not have, and a link that lands in the wrong place is
                       worse than a name that says where to look. -->
                  <div class="text-xs text-gray-500 mt-2 leading-snug">{{ $t("user_modal.virtual_device_open_device") }}</div>
                </template>
              </template>
              <div class="text-xs text-gray-500" v-else>{{ $t("user_modal.virtual_device_none") }}</div>
            </div>

            <!-- One person, one device (a device per department would fan one
                 SOS into several events), so someone has to say which department
                 it lives in. Only shown when there is actually a choice.
                 The options follow the departments and customers ticked in this
                 form, not the saved record, so a department added in the same
                 edit can be chosen at once and one unticked stops being
                 offered. Grouped by customer: two customers' "Lager" read alike,
                 and choosing the wrong one is a move that resets the device. -->
            <div class="mt-3" v-if="virtualDeviceEnabled && virtualDeviceUnitOptions.length > 1">
              <div class="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1">{{ $t("user_modal.virtual_device_unit") }}</div>
              <select v-model="virtualDeviceUnit" class="bg-gray-100 rounded w-full text-xs text-gray-900 focus:outline-none border-b border-gray-300 px-2 py-2">
                <option :value="null">{{ $t("user_modal.virtual_device_unit_auto") }}</option>
                <optgroup v-for="g in virtualDeviceUnitGroups" :key="g.key" :label="g.customer_name">
                  <option v-for="u in g.units" :key="u.unit_id" :value="u.unit_id">{{ u.name }}</option>
                </optgroup>
              </select>
              <div class="text-xs text-gray-500 mt-1 leading-snug">{{ $t("user_modal.virtual_device_unit_help") }}</div>
            </div>
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="updateInfo.role == 'alarm_center'">
            <BaseSelect v-model="updateInfo.alarm_center_id" :options="alarm_centers" nameSelector="name" optionSelector="_id" :field_name="$t('user_role.alarm_center')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="updateInfo.role == 'seller'">
            <BaseSelect v-model="updateInfo.seller_id" :options="sellers" nameSelector="name" optionSelector="_id" :field_name="$t('user_role.seller')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="updateInfo.role == 'tech'">
            <BaseSelect v-model="updateInfo.tech_id" :options="technicians" nameSelector="name" optionSelector="_id" :field_name="$t('user_role.tech')" rules="required" />
          </div>
        </div>
        <!-- SEC-329: reset this user's password. Admin only, and never on the
             admin's own record — that would lock them out of the session they
             are working in. -->
        <div class="mx-2 border-b border-secondary-400 my-5" v-if="canResetPassword"></div>
        <div class="flex flex-wrap bg-gray-50 p-2 rounded" v-if="canResetPassword">
          <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("user_modal.password_reset_title") }}</div>

          <div class="w-full px-2 py-1" v-if="!passwordResetConfirming">
            <button type="button" class="btn-blue-outline px-3 py-2" @click="startPasswordReset()">{{ $t("user_modal.reset_password") }}<BaseIcon icon="key" class="ml-1" /></button>
            <div class="text-xs italic text-gray-500 mt-2">{{ $t("user_modal.reset_password_hint") }}</div>
            <div class="text-xs text-gray-500 mt-1" v-if="updateInfo.passwordResetAt">
              {{ $t("user_modal.last_reset", { by: updateInfo.passwordResetBy, at: formatResetTime(updateInfo.passwordResetAt) }) }}
            </div>
          </div>

          <div class="w-full px-2 py-1" v-else>
            <div class="text-sm text-gray-700">{{ $t("user_modal.reset_password_confirm", { email: updateInfo.email }) }}</div>
            <div class="mt-2">
              <button type="button" class="btn-green px-3 py-2 mr-2" :disabled="passwordResetSending" @click="confirmPasswordReset()">{{ $t("user_modal.reset_password_send") }}</button>
              <button type="button" class="btn-red-outline px-3 py-2" :disabled="passwordResetSending" @click="cancelPasswordReset()">{{ $t("user_actions.cancel") }}</button>
            </div>
          </div>
        </div>
      </ValidationObserver>
    </div>
    <div class="modal-footer">
      <div class="footer-container clearfix">
        <button class="btn-red-outline px-3 py-2 float-left" @click="hide('update-user')">{{ $t("user_actions.cancel") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
        <button class="btn-green px-3 py-2 float-right" @click="updateUser()">{{ $t("user_actions.save") }}<BaseIcon icon="save" class="ml-1" /></button>
      </div>
    </div>

    <!-- A save that moves the mobile alarm device to another customer resets
         its routing (recipients, SMS, message settings) and takes it off the
         old customer's recipient lists — securcloud does that on the move,
         without asking, because nobody is there to ask. This is the asking.
         Laid over the whole modal, as the device window's move question is. -->
    <div class="fixed inset-0 flex items-center justify-center p-5" style="background: rgba(0, 0, 0, 0.45); z-index: 1000" v-if="virtualDeviceMoveConfirming">
      <div class="bg-white rounded shadow-lg p-5 w-full" style="max-width: 460px">
        <div class="text-base font-bold text-gray-800 font-serif mb-2">{{ $t("user_modal.virtual_device_move_customer_title") }}</div>
        <!-- Two wordings: the move is certain (every department securcloud
             could pick is under another customer), or it is possible (some
             are, and which one wins is securcloud's choice, not this form's).
             The second says how to take the guess out: choose the department. -->
        <div class="text-xs text-gray-600 mb-4" v-if="virtualDeviceMoveCertain">{{ $t("user_modal.virtual_device_move_customer_text", { from: virtualDeviceMoveFromName }) }}</div>
        <div class="text-xs text-gray-600 mb-4" v-else>{{ $t("user_modal.virtual_device_move_customer_maybe_text", { from: virtualDeviceMoveFromName }) }}</div>
        <div class="clearfix">
          <button type="button" class="btn-green px-3 py-2 mr-2 mb-1" @click="confirmVirtualDeviceMove()">{{ virtualDeviceMoveCertain ? $t("user_modal.virtual_device_move_customer_confirm") : $t("user_modal.virtual_device_move_customer_maybe_confirm") }}</button>
          <button type="button" class="btn-red-outline px-3 py-2 mb-1" @click="virtualDeviceMoveConfirming = false">{{ $t("user_actions.cancel") }}</button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { hasMobileAlarmAddon } from "@/utils/virtualDevice";

const MODAL_WIDTH = 800;

/*
Where securcloud will put the mobile alarm device after a save, as the set of
departments it could land in: a model of pickUnit + evaluate in
alarms/services/virtual_device.js, which runs on every updateUser.

pickUnit is not "the chosen department, else the current one". A department
only counts while it ENTITLES — the addon on the department or on its
customer — and the order is:
  1. the chosen department, if it entitles;
  2. the department the device is in now, if it still entitles;
  3. the first department carrying the addon itself;
  4. the first department whose customer carries it;
  5. nothing entitles: the person is not entitled, the device is suspended
     where it is and does not move.
"First" in 3 and 4 is database order, which nobody promises, so any of the
candidates is possible. And an entitlement this page cannot see (null) is
both possible and not certain: the department is a candidate, and the walk
goes on past it.

Each unit: { unit_id, parent_id (the customer's _id, or null when not known),
unitAddon, customerAddon } with the two verdicts true / false / null.
Returns { targets, mayStay }: every department the device could end up in,
and whether it could end up not moving at all (step 5).
*/
function predictVirtualDevicePlacement(units, { pickedId = null, currentId = null } = {}) {
  const list = Array.isArray(units) ? units : [];
  const entitles = (u) => {
    if (u.unitAddon === true || u.customerAddon === true) return true;
    if (u.unitAddon === false && u.customerAddon === false) return false;
    return null;
  };

  const targets = [];
  let settled = false;
  const consider = (candidates, verdictOf) => {
    if (settled) return;
    for (const u of candidates) {
      const verdict = verdictOf(u);
      if (verdict === false) continue;
      targets.push(u);
      if (verdict === true) settled = true;
    }
  };

  const picked = pickedId ? list.find((u) => u.unit_id === pickedId) : null;
  const current = currentId ? list.find((u) => u.unit_id === currentId) : null;

  // 1 and 2 each name one department.
  if (picked) consider([picked], entitles);
  if (current) consider([current], entitles);
  // 3: any department carrying the addon itself could come first.
  consider(list, (u) => u.unitAddon);
  // 4: any department whose customer carries it could come first.
  consider(list, (u) => u.customerAddon);

  return { targets: targets.filter((u, i) => targets.indexOf(u) === i), mayStay: !settled };
}

export default {
  props: {
    userInfo: {
      required: true,
      default: null,
    },
    roles: {
      required: true,
    },
    allCustomers: {
      required: true,
    },
    allUnits: {
      required: true,
    },
    alarm_centers: {
      required: true,
    },
    resellers: {
      required: true,
    },
    sellers: {
      required: true,
    },
    technicians: {
      required: true,
    },
  },
  data() {
    return {
      updateInfo: null,
      // SEC-329: the password reset asks once before it sends
      passwordResetConfirming: false,
      passwordResetSending: false,
      customerSearch: "",
      unitSearch: "",
      filtered_roles: [],
      customers: [],
      units: [],
      customer_units: [],
      // The panel's own state. Read from the server because it is about an
      // alarms row, which changes when an addon changes rather than when this
      // form is saved.
      virtualDevice: null,
      virtualDeviceUnits: [],
      virtualDeviceUnit: null,
      virtualDeviceLoading: false,
      // Loaded is separate from "has a device": the panel must render its
      // "none yet" line for exactly the people who have none, which a guard on
      // the device itself can never do.
      virtualDeviceLoaded: false,
      virtualDeviceError: false,
      // The cross-customer move question (see virtualDeviceCrossCustomerMove)
      virtualDeviceMoveConfirming: false,
      virtualDeviceMoveFromName: "",
      virtualDeviceMoveCertain: true,
    };
  },
  watch: {
    // SEC-329: a different record means the pending confirm no longer applies.
    // A separate watcher so the existing userInfo watcher stays as it is.
    updateInfo() {
      this.passwordResetConfirming = false;
      this.passwordResetSending = false;
      this.virtualDeviceMoveConfirming = false;
    },
    /*
    A chosen department the administrator unticks in this edit — the
    department itself, or the customer it is under — is cleared from the
    picker. Left set, it was sent and securcloud dropped it without a word,
    and the stored choice survived the save.

    Only a choice that WAS on offer and no longer is: never one that was not
    on offer to begin with. The options are built from the units this page has
    loaded, which for some viewers is a subset of what securcloud allows, and
    clearing on that difference would wipe a valid choice the moment the modal
    opened. The user id rules out a switch to another record.
    */
    virtualDeviceUnitScope(now, before) {
      if (!this.virtualDeviceUnit || !before || now.userId !== before.userId) return;
      if (before.ids.indexOf(this.virtualDeviceUnit) > -1 && now.ids.indexOf(this.virtualDeviceUnit) === -1) {
        this.virtualDeviceUnit = null;
      }
    },
    userInfo(newVal) {
      // eslint-disable-line
      this.updateInfo = _.cloneDeep(newVal);

      this.customerSearch = "";
      this.unitSearch = "";
      this.customer_units = [];
      // SecurOne basic users store customer and units as ids (the app's convention); the pickers work in codes
      if (/^(basic_user|securone_basic)$/.test(this.updateInfo.role)) {
        this.updateInfo.customers_id = (this.updateInfo.customers_id || []).map((v) => { const c = this.customers.find((x) => String(x._id) === String(v)); return c ? c.customer_id : v; });
        this.updateInfo.units_id = (this.updateInfo.units_id || []).map((v) => { const u = this.units.find((x) => String(x._id) === String(v)); return u ? u.unit_id : v; });
      }
      if (this.updateInfo.role == "superuser" || this.updateInfo.role == "user" || this.updateInfo.role == "securdesktop" || this.updateInfo.role == "securone_basic" || this.updateInfo.role == "basic_user") {
        if (this.updateInfo.reseller_id != "" && this.user.role == "admin") this.getResellerCustomers();
        this.filterUnitByCustomer();
      }

      this.virtualDevice = null;
      this.virtualDeviceUnits = [];
      this.virtualDeviceUnit = this.updateInfo.virtual_device_unit || null;
      this.virtualDeviceLoaded = false;
      this.virtualDeviceError = false;
      // Reset with the rest: a role change inside the modal, from one that
      // fetches to one that does not, otherwise left "Loading…" on screen
      // for good, because the fetch that would clear it never starts.
      this.virtualDeviceLoading = false;
      if (this.hasVirtualDevicePanel) this.getVirtualDevice();
    },
    allCustomers(newVal) {
      this.customers = _.cloneDeep(newVal);
    },
    allUnits(newVal) {
      this.units = _.cloneDeep(newVal);
    },
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    virtualDeviceUnitName() {
      if (!this.virtualDevice) return "";
      let unit = _.find(this.virtualDeviceUnits, { unit_id: this.virtualDevice.parent_id }) || _.find(this.allUnits, { unit_id: this.virtualDevice.parent_id });
      return unit ? unit.name : this.virtualDevice.parent_id;
    },
    // The roles securcloud provisions a device for (virtual_device.js
    // DEVICE_ROLES). Everyone else has no device and no panel.
    hasVirtualDevicePanel() {
      return !!this.updateInfo && ["user", "superuser", "basic_user"].indexOf(this.updateInfo.role) > -1;
    },
    /**
     * The departments the device can be placed in, from this form as it is now.
     *
     * The same set securcloud picks from (virtualDevice.unitsForUser): the
     * person's own departments, or — for a customer-level login, which carries
     * none (the save empties units_id for a superuser) — every department under
     * their customers. Built here rather than read from the server's answer,
     * which describes the record as it was saved when the modal opened.
     *
     * A unit this page never loaded is named from the server's list when it
     * has it, and left out otherwise: there is no name to show for it.
     *
     * The stored choice stays on offer while its customer is ticked, even
     * when the customer path did not list it. That path draws from the units
     * this page holds, which an admin's reseller filter narrows to that
     * reseller's departments, while securcloud allows every department under
     * the customer; without this the select showed blank for a choice that is
     * valid and still saved. Its customer unticked, it drops out like any
     * other option, so the virtualDeviceUnitScope watcher still clears it.
     */
    virtualDeviceUnitOptions() {
      if (!this.updateInfo) return [];
      const { selected, byCustomers } = this.virtualDeviceUnitSource;

      const known = (unitId) => _.find(this.units, { unit_id: unitId }) || _.find(this.allUnits, { unit_id: unitId }) || _.find(this.virtualDeviceUnits, { unit_id: unitId });
      let units = byCustomers ? this.customer_units : selected.map(known).filter(Boolean);

      // The STORED choice, not the select's current value: picking another
      // option must not make the saved one vanish from the list.
      const stored = this.updateInfo.virtual_device_unit;
      if (byCustomers && stored && !_.find(units, { unit_id: stored })) {
        const full = _.find(this.allUnits, { unit_id: stored });
        if (full) {
          const ticked = (this.updateInfo.customers_id || []).map((key) => this.customerByKey(key)).filter(Boolean).map((c) => String(c._id));
          if (ticked.indexOf(String(full.parent_id)) > -1) units = [...units, full];
        } else {
          // Not a unit this page knows at all: the server's word, ungrouped.
          const fromServer = _.find(this.virtualDeviceUnits, { unit_id: stored });
          if (fromServer) units = [...units, fromServer];
        }
      }

      return _.uniqBy(units, "unit_id").map((u) => ({ unit_id: u.unit_id, name: u.name, parent_id: u.parent_id ? String(u.parent_id) : "" }));
    },
    // The ticked departments, and whether securcloud picks from every
    // department under the ticked customers instead: a customer-level login
    // (the save empties its units_id), or nothing ticked.
    virtualDeviceUnitSource() {
      const selected = (this.updateInfo.units_id || []).filter(Boolean);
      return { selected, byCustomers: this.updateInfo.role == "superuser" || selected.length === 0 };
    },
    // The options under their customer, so two customers' same-named
    // departments cannot be mistaken for each other.
    virtualDeviceUnitGroups() {
      const byParent = _.groupBy(this.virtualDeviceUnitOptions, "parent_id");
      return _.map(byParent, (units, parentId) => ({
        key: parentId || "-",
        customer_name: this.customerNameForUnitParent(parentId),
        units: _.sortBy(units, (u) => (u.name || "").toLowerCase()),
      }));
    },
    // What the options watcher compares: which record, and which unit ids.
    virtualDeviceUnitScope() {
      return {
        userId: this.updateInfo ? this.updateInfo._id : null,
        ids: this.virtualDeviceUnitOptions.map((u) => u.unit_id),
      };
    },
    /**
     * The alarm centre the device resolved to, by name.
     *
     * Resolved once at provisioning and stored on the row, so this is what the
     * alarm would actually be sent to — not what the department's configuration
     * says today. Showing the id would tell an administrator nothing.
     */
    virtualDeviceAlarmCentreName() {
      if (!this.virtualDevice || !this.virtualDevice.alarm_central) return this.$t("user_modal.virtual_device_no_alarm_centre");
      // Named by the server, for every role: `alarm_centers` is loaded only for
      // admin and reseller, so a superuser or seller used to read a raw id here.
      if (this.virtualDevice.alarm_central_name) return this.virtualDevice.alarm_central_name;
      let centre = _.find(this.alarm_centers, { _id: this.virtualDevice.alarm_central });
      return centre ? centre.name : this.virtualDevice.alarm_central;
    },
    /*
    The D1b opt flag, as a checkbox.

    Stored as 'auto' | 'off' rather than a boolean so the field says what it
    means in the database, and read through a computed because updateInfo is a
    clone made in a watcher — binding the input straight at it would write into
    the object the parent list still holds.

    Absent reads as ticked: that is the schema default, and it is what every
    record written before this existed should behave as.

    $set because the key may not be on the cloned object at all, and Vue 2 does
    not make a property added by plain assignment reactive — the box would tick
    once and then stop responding.
    */
    virtualDeviceEnabled: {
      get() {
        if (!this.updateInfo) return true;
        return this.updateInfo.virtual_device !== "off";
      },
      set(value) {
        if (!this.updateInfo) return;
        this.$set(this.updateInfo, "virtual_device", value ? "auto" : "off");
      },
    },
    // SEC-329: only an admin resets passwords, and not on their own record.
    // Usernames are unique, so they identify the record without an id.
    canResetPassword() {
      if (!this.updateInfo) return false;
      if (this.user.role !== "admin") return false;

      return this.updateInfo.username !== this.user.username;
    },
    // SEC-334: what the checkbox list may draw. The customers this reseller sells, plus any
    // still stored on the user that it no longer sells. That second group exists whenever a
    // customer is moved to another seller after being given to a user. It is filtered out of
    // this.customers, so without this it never renders, yet it is still saved with every edit
    // and keeps the user's access to another brand's customer.
    selectableCustomers() {
      const vm = this;
      const stranded = _.filter(this.allCustomers, function(o) {
        return vm.updateInfo.customers_id.indexOf(o.customer_id) > -1 && !_.some(vm.customers, { customer_id: o.customer_id });
      });
      return this.customers.concat(stranded);
    },
    filteredCustomer() {
      var vm = this;
      let c = _.filter(this.selectableCustomers, function(o) {
        return o.name.toLowerCase().includes(vm.customerSearch.toLowerCase()) || vm.updateInfo.customers_id.indexOf(o.customer_id) > -1;
      });
      return c;
    },
    filteredUnit() {
      var vm = this;
      let u = _.filter(this.customer_units, function(o) {
        return o.name.toLowerCase().includes(vm.unitSearch.toLowerCase()) || vm.updateInfo.units_id.indexOf(o.unit_id) > -1;
      });
      return u;
    },
    // Units (search-filtered) grouped under their parent customer so a
    // multi-customer 'user' sees which verksamhet belongs to which customer.
    groupedFilteredUnits() {
      let byParent = _.groupBy(this.filteredUnit, (u) => String(u.parent_id));
      return _.map(byParent, (units, parentId) => {
        let c = _.find(this.customers, (x) => String(x._id) == parentId);
        return { customer_id: parentId, customer_name: c ? c.name : "", units };
      });
    },
    // True when every verksamhet currently listed is ticked. Scoped to the
    // listed units rather than to every unit of the kund, so the control can
    // never tick a verksamhet the search has filtered away.
    allListedUnitsSelected() {
      if (this.filteredUnit.length == 0) return false;
      let listedIds = _.map(this.filteredUnit, "unit_id");
      return _.every(listedIds, (id) => this.updateInfo.units_id.indexOf(id) > -1);
    },
  },
  methods: {
    // SEC-329: ask once before sending, so the button cannot be hit by accident.
    startPasswordReset() {
      this.passwordResetConfirming = true;
    },

    cancelPasswordReset() {
      this.passwordResetConfirming = false;
    },

    // Send the reset. Everything about the credential is decided on the
    // server; the reply carries nothing that could be used to log in.
    async confirmPasswordReset() {
      this.passwordResetSending = true;

      try {
        await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/user/${this.updateInfo._id}/reset_password`);

        this.passwordResetConfirming = false;
        this.handleSuccess(this.$t("user_modal.reset_password_sent", { email: this.updateInfo.email }));

        // show it on the record right away, without reopening the modal
        this.$set(this.updateInfo, "passwordResetBy", this.user.username);
        this.$set(this.updateInfo, "passwordResetAt", new Date().toISOString());
      }
      catch (error) {
        // the server explains what went wrong (no email on the user, mail
        // could not be sent); fall back to the generic message
        let message = this.$t("problem_occured");
        if (error.response && error.response.data && error.response.data.message) {
          message = error.response.data.message;
        }
        this.handleError(error, message);
      }

      this.passwordResetSending = false;
    },

    formatResetTime(value) {
      if (!value) return "";
      return this.moment(value).format("YYYY-MM-DD, HH:mm");
    },

    getVirtualDevice() {
      this.virtualDeviceLoading = true;
      this.virtualDeviceError = false;

      // The record this answer belongs to. Open A, then B before A's answer
      // lands, and A's device would otherwise be painted onto B's panel.
      const forId = this.updateInfo._id;

      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/user/${forId}/virtual-device`)
        .then((response) => {
          if (!this.updateInfo || this.updateInfo._id !== forId) return;
          this.virtualDevice = response.data.virtual_device;
          this.virtualDeviceUnits = response.data.units || [];
          this.virtualDeviceUnit = response.data.chosen_unit || null;
          this.virtualDeviceLoaded = true;
        })
        .catch(() => {
          if (!this.updateInfo || this.updateInfo._id !== forId) return;
          // The panel is informational; a failure here must not stop somebody
          // editing the rest of the record. But it is a failure, and said so.
          this.virtualDevice = null;
          this.virtualDeviceError = true;
          this.virtualDeviceLoaded = true;
        })
        .finally(() => {
          if (!this.updateInfo || this.updateInfo._id !== forId) return;
          this.virtualDeviceLoading = false;
        });
    },

    // A customer by either key this form holds: customer_id (the pickers) or
    // _id (a unit's parent_id).
    customerByKey(key) {
      const matches = (c) => c.customer_id == key || String(c._id) == String(key);
      return _.find(this.allCustomers, matches) || _.find(this.customers, matches);
    },

    customerNameForUnitParent(parentId) {
      if (!parentId) return "";
      const customer = _.find(this.allCustomers, (c) => String(c._id) == String(parentId)) || _.find(this.customers, (c) => String(c._id) == String(parentId));
      return customer ? customer.name : "";
    },

    /*
    The departments securcloud will choose the device's department from after
    this save, described for predictVirtualDevicePlacement.

    The same set as its unitsForUser, from this form as it will be sent: the
    ticked departments, or — for a customer-level login, which the save sends
    with none — every department under the ticked customers. Drawn from
    `allUnits`, the page's full list, not `units`, which an admin's reseller
    filter narrows (getResellerCustomers); securcloud is not narrowed.

    What the page cannot see is kept as "unknown", never dropped, because a
    department nobody looked at is exactly where a silent move goes:
     - a ticked department id the page has no row for: customer and addon
       unknown;
     - a ticked customer the page cannot place, or — for a reseller, whose
       /units holds only its own departments — the rest of a ticked customer:
       one stand-in department, addon unknown.
    Customer addons are known only where /customer sent them (admin, seller).

    One inference fills the commonest gap. An ACTIVE device is in a department
    that entitled it at the last ensure, and every addon change re-runs that
    ensure (reconcileForUnits), so when its department carries no addon of its
    own, its customer does. That holds for user and superuser; the one active
    device that is not entitled is a basic_user's invited with `force`, and a
    basic_user has exactly one customer, so it never moves between customers.
    */
    virtualDevicePlacementUnits() {
      const pageUnit = (id) => _.find(this.allUnits, { unit_id: id }) || _.find(this.units, { unit_id: id });
      const customerAddon = (parentId) => (parentId ? hasMobileAlarmAddon(this.customerByKey(parentId)) : null);
      const describe = (u) => ({
        unit_id: u.unit_id,
        parent_id: u.parent_id ? String(u.parent_id) : null,
        unitAddon: hasMobileAlarmAddon(u),
        customerAddon: customerAddon(u.parent_id),
      });
      const unknown = (unitId, parentId) => ({ unit_id: unitId || null, parent_id: parentId || null, unitAddon: null, customerAddon: parentId ? customerAddon(parentId) : null });

      const { selected, byCustomers } = this.virtualDeviceUnitSource;

      let placement = [];
      if (!byCustomers) {
        placement = selected.map((id) => {
          const u = pageUnit(id);
          return u ? describe(u) : unknown(id, null);
        });
      } else {
        const everyUnit = this.allUnits && this.allUnits.length ? this.allUnits : this.units || [];
        const partialUnitList = this.user.role == "reseller";
        (this.updateInfo.customers_id || []).filter(Boolean).forEach((key) => {
          const customer = this.customerByKey(key);
          if (!customer) {
            placement.push(unknown(null, null));
            return;
          }
          const parentId = String(customer._id);
          everyUnit.filter((u) => String(u.parent_id) === parentId).forEach((u) => placement.push(describe(u)));
          if (partialUnitList) placement.push(unknown(null, parentId));
        });
      }

      // The choice this form will send, when the page cannot place it (the
      // server's stored choice, shown from its own list): a candidate whose
      // customer is unknown, not a choice securcloud would ignore.
      if (this.virtualDeviceUnit && !_.find(placement, { unit_id: this.virtualDeviceUnit })) {
        placement.push(unknown(this.virtualDeviceUnit, null));
      }

      // The inference above, for the device's own customer. Read from the
      // department the device is in, ticked or not: it is a fact about the
      // customer, and it matters most when that department is being unticked.
      const device = this.virtualDevice;
      const deviceUnit = device && device.active && device.parent_id ? pageUnit(device.parent_id) : null;
      if (deviceUnit && deviceUnit.parent_id && hasMobileAlarmAddon(deviceUnit) === false && customerAddon(deviceUnit.parent_id) === null) {
        const entitledCustomer = String(deviceUnit.parent_id);
        placement = placement.map((u) => (u.parent_id === entitledCustomer && u.customerAddon === null ? { ...u, customerAddon: true } : u));
      }
      return placement;
    },

    /*
    Would this save move the mobile alarm device out of its customer?

    Answered from securcloud's own rule (predictVirtualDevicePlacement), not
    from "the chosen department, else the current one": a department without
    the addon is passed over, so an explicit choice of one, or a current
    department unticked with nothing else of its customer entitled, sends the
    device wherever the addon is — possibly another customer, where the move
    wipes its recipients and SMS setup. And a stored choice under another
    customer that does NOT entitle moves nothing while the current department
    still does, so it must not be asked about on every save.

    Returns null for no question, or { from, certain }: `from` names the
    customer it would leave; `certain` is true when every department it could
    land in is under another customer, false when only some are (database
    order, or an addon this page cannot see, decides). Not asked when the
    device's own department is unknown to this page: there is no customer to
    name, and nothing to compare with.
    */
    virtualDeviceCrossCustomerMove() {
      if (!this.hasVirtualDevicePanel || !this.virtualDeviceEnabled || !this.virtualDevice || !this.virtualDevice.parent_id) return null;
      // An inactive account is suspended where it is (evaluate: user_inactive).
      if (this.updateInfo.status && this.updateInfo.status !== "active") return null;

      const currentUnit = _.find(this.allUnits, { unit_id: this.virtualDevice.parent_id }) || _.find(this.units, { unit_id: this.virtualDevice.parent_id });
      if (!currentUnit || !currentUnit.parent_id) return null;
      const fromCustomer = String(currentUnit.parent_id);

      const { targets, mayStay } = predictVirtualDevicePlacement(this.virtualDevicePlacementUnits(), {
        pickedId: this.virtualDeviceUnit || null,
        currentId: currentUnit.unit_id,
      });

      const leaving = targets.filter((u) => u.parent_id !== fromCustomer);
      if (leaving.length === 0) return null;

      return {
        from: this.customerNameForUnitParent(fromCustomer) || currentUnit.name || fromCustomer,
        certain: !mayStay && leaving.length === targets.length && leaving.every((u) => !!u.parent_id),
      };
    },

    confirmVirtualDeviceMove() {
      this.virtualDeviceMoveConfirming = false;
      this.updateUser(true);
    },

    async updateUser(moveConfirmed = false) {
      this.$refs.formUpdateUser.validate().then(async (success) => {
        if (!success) {
          return;
        }
        if (this.updateInfo.role == "user" && this.updateInfo.units_id.length == 0) {
          this.handleError(null, "Ingen verksamhet vald");
          return;
        }
        if ((this.updateInfo.role == "securdesktop" || this.updateInfo.role == "securone_basic" || this.updateInfo.role == "basic_user") && this.updateInfo.units_id.length == 0) {
          this.handleError(null, "Ingen verksamhet vald");
          return;
        }
        if ((this.updateInfo.role == "securone_basic" || this.updateInfo.role == "basic_user") && this.updateInfo.customers_id.filter(Boolean).length != 1) {
          this.handleError(null, "En SecurOne-användare tillhör exakt en kund");
          return;
        }
        if (this.updateInfo.role == "securone_basic" && this.updateInfo.customers_id.filter(Boolean).length != 1) {
          this.handleError(null, "En SecurOne-användare tillhör exakt en kund");
          return;
        }
        if (this.updateInfo.role == "superuser" && this.updateInfo.customers_id.length == 0) {
          this.handleError(null, "Ingen kund vald");
          return;
        }
        
        if (this.updateInfo.role == 'admin' && (!this.updateInfo.accessCountries || this.updateInfo.accessCountries.length == 0)) {
          this.handleError(null, "Välj minst en domän (Sverige eller Norge)");
          return;
        }

        // Asked after every other check, so the question is never followed by
        // a refusal, and before the fields below are trimmed for the role.
        if (moveConfirmed !== true) {
          const move = this.virtualDeviceCrossCustomerMove();
          if (move) {
            this.virtualDeviceMoveFromName = move.from;
            this.virtualDeviceMoveCertain = move.certain;
            this.virtualDeviceMoveConfirming = true;
            return;
          }
        }

        if (this.updateInfo.role != "superuser" && this.updateInfo.role != "user" && this.updateInfo.role != "securdesktop" && this.updateInfo.role != "securone_basic" && this.updateInfo.role != "basic_user") this.updateInfo.customers_id = [];
        if (this.updateInfo.role != "user" && this.updateInfo.role != "securdesktop" && this.updateInfo.role != "securone_basic" && this.updateInfo.role != "basic_user") this.updateInfo.units_id = [];
        if (this.updateInfo.role != "alarm_center") this.updateInfo.alarm_center_id = "";
        if (this.updateInfo.role != "reseller" && this.updateInfo.role != "superuser" && this.updateInfo.role != "user" && this.updateInfo.role != "alarm_center" && this.updateInfo.role != "securdesktop") this.updateInfo.reseller_id = "";
        if (this.updateInfo.role != "seller") this.updateInfo.seller_id = "";
        if (this.updateInfo.role != "tech") this.updateInfo.tech_id = "";

        let data = {
          firstname: this.updateInfo.firstname,
          lastname: this.updateInfo.lastname,
          username: this.updateInfo.username.toLowerCase(),
          email: this.updateInfo.email,
          phone: this.updateInfo.phone,
          role: this.updateInfo.role,
          // customer_id: this.updateInfo.customer_id,
          customers_id: this.updateInfo.customers_id,
          units_id: this.updateInfo.units_id,
          alarm_center_id: this.updateInfo.alarm_center_id,
          reseller_id: this.updateInfo.reseller_id,
          seller_id: this.updateInfo.seller_id,
          tech_id: this.updateInfo.tech_id,
          accessCountries: this.updateInfo.role == 'admin' ? this.updateInfo.accessCountries : undefined,
          virtual_device: this.virtualDeviceEnabled ? "auto" : "off",
          // "" clears the choice back to "decide from the departments"; the
          // server refuses a department this person does not belong to.
          virtual_device_unit: this.virtualDeviceUnit || "",
        };
        try {
          // eslint-disable-next-line
          let response = await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/user/${this.updateInfo._id}`, data);
          this.$emit("updateUser");
          this.$modal.hide("update-user");
        } catch (error) {
          let error_message = "Något problem uppstod";
          if (error.response && error.response.status == 400) error_message = "Otillräckliga behörigheter";
          // A 422 is securcloud refusing one field and saying which (a
          // SecurOne user outside one customer, a device department the person
          // no longer belongs to). Its message names the fix; "something went
          // wrong" left the administrator guessing what to change.
          if (error.response && error.response.status == 422 && error.response.data && error.response.data.message) error_message = error.response.data.message;
          // The device department refusal (audit 3, item 38) is ours to word:
          // securcloud's message is English, and this one is said in the
          // administrator's language. Nothing was saved, so the form stays
          // open with the choice in it to change.
          if (error.response && error.response.status == 422 && error.response.data && error.response.data.errorId === "virtual_device_unit_not_allowed") error_message = this.$t("user_modal.virtual_device_unit_not_allowed");
          this.handleError(error, error_message);
        }
      });
    },

    filterUnitByCustomer() {
      if (this.updateInfo.customers_id.length == 0) {
        this.customer_units = [];
        return;
      }

      // Union of units across ALL selected customers (role 'user' may span
      // multiple customers; 'securdesktop' resolves to a single customer).
      let parentIds = _.map(
        _.filter(this.customers, (c) => this.updateInfo.customers_id.indexOf(c.customer_id) > -1),
        (c) => String(c._id)
      );
      this.customer_units = _.filter(this.units, (u) => parentIds.indexOf(String(u.parent_id)) > -1);
    },

    // Drop any selected unit that no longer belongs to a selected customer
    // (only call on customer-checkbox change, never in the open/watch path).
    pruneUnits() {
      let validUnitIds = _.map(this.customer_units, "unit_id");
      this.updateInfo.units_id = _.filter(this.updateInfo.units_id, (id) => validUnitIds.indexOf(id) > -1);
    },

    // "Select all" control: tick every verksamhet currently listed, or untick
    // them when they were all ticked already. With no search term the list is
    // the whole kund, so this still selects or clears everything. With a search
    // term it only touches what the list shows — a verksamhet filtered away is
    // never granted by accident.
    toggleAllUnits() {
      let listedIds = _.map(this.filteredUnit, "unit_id");

      if (this.allListedUnitsSelected) {
        this.updateInfo.units_id = _.difference(this.updateInfo.units_id, listedIds);
      } else {
        this.updateInfo.units_id = _.union(this.updateInfo.units_id, listedIds);
      }
    },

    changeUserRole() {
      this.changeReseller();
    },

    changeReseller() {
      if (!/^reseller|^superuser|^user|^securdesktop/.test(this.updateInfo.role)) {
        if (this.updateInfo.reseller_id != "") {
          this.getResellerCustomers();
        } else {
          this.customers = _.cloneDeep(this.allCustomers);
          this.units = _.cloneDeep(this.allUnits);
        }

        this.updateInfo.customers_id = [];
        this.updateInfo.units_id = [];
        this.customer_units = [];
      }
    },

    // SEC-334: stored on this user, but the chosen reseller does not sell it. Marked in the list
    // so an admin can see it and untick it; nothing is removed without that click.
    isStrandedCustomer(customer) {
      return !_.some(this.customers, { customer_id: customer.customer_id });
    },

    getResellerCustomers() {
      var vm = this;

      this.units = _.filter(this.allUnits, { seller_id: this.updateInfo.reseller_id });
      let customerIDs = _.uniq(_.map(this.units, "parent_id"));
      this.customers = _.filter(this.allCustomers, function(o) {
        return customerIDs.indexOf(o._id) > -1 || o.seller == vm.updateInfo.reseller_id;
      });
    },

    filterRoles() {
      if (this.user.role !== "admin" && this.user.role !== "reseller") {
        this.filtered_roles = _.filter(this.roles, function(o) {
          return o.id == "superuser" || o.id == "user" || o.id == "securdesktop";
        });
      } 
      else if (this.user.role === "reseller") {
        this.filtered_roles = _.filter(this.roles, function(o) {
          return o.id == "superuser" || o.id == "user" || o.id == "alarm_center";
        });
      } 
      else {
        this.filtered_roles = this.roles;
      }
    },

    resetForm() {
      this.$refs.formUpdateUser.reset();
      this.customers = _.cloneDeep(this.allCustomers);
      this.units = _.cloneDeep(this.allUnits);
      this.filterUnitByCustomer();
    },

    hide(id) {
      this.$modal.hide(id);
    },
  },

  created() {
    this.modalWidth = window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH;
    this.filterRoles();
  },
};
</script>
